import { FormEvent, useEffect, useState } from 'react';
import type { CSSProperties } from 'react';
import QRCode from 'qrcode';
import { createTvPairing, loadTvPairing } from '../core/api';
import type { AdCreative, PartnerTheme, Session, TvPairing } from '../types';
import { BrandMark } from './BrandMark';

interface Props {
  busy: boolean;
  error: string;
  theme: PartnerTheme;
  takeover: AdCreative | null;
  onAdImpression: (ad: AdCreative) => void;
  onSubmit: (email: string, password: string) => void;
  onPaired: (session: Session) => void;
}

export function LoginScreen({ busy, error, theme, takeover, onAdImpression, onSubmit, onPaired }: Props) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [pairing, setPairing] = useState<TvPairing | null>(null);
  const [qrImage, setQrImage] = useState('');
  const [pairingError, setPairingError] = useState('');

  useEffect(() => {
    let active = true;
    let timer: number | undefined;
    const start = async () => {
      try {
        const next = await createTvPairing(theme.slug);
        if (!active) return;
        setPairing(next);
        const activation = new URL(window.location.origin + window.location.pathname);
        activation.searchParams.set('pair', next.code);
        activation.searchParams.set('partner', theme.slug);
        setQrImage(await QRCode.toDataURL(activation.toString(), {
          width: 260, margin: 1,
          color: { dark: '#09090b', light: '#ffffff' },
        }));
        timer = window.setInterval(async () => {
          try {
            const status = await loadTvPairing(next.code);
            if (!active) return;
            if (status.status === 'approved') {
              window.clearInterval(timer);
              onPaired(status.session);
            } else if (status.status === 'expired' || status.status === 'consumed') {
              window.clearInterval(timer);
              setPairingError('O QR Code expirou. Recarregue a página para gerar outro.');
            }
          } catch {
            // Falhas transitórias de polling não devem remover o fallback por senha.
          }
        }, 2_000);
      } catch {
        if (active) setPairingError('QR indisponível agora. Use e-mail e senha.');
      }
    };
    void start();
    return () => { active = false; if (timer) window.clearInterval(timer); };
  }, [onPaired, theme.slug]);

  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (email && password) onSubmit(email.trim(), password);
  };

  const signupUrl = (() => {
    if (!pairing) return null;
    const url = new URL('/signup', import.meta.env.VITE_WEBPLAYER_URL || 'https://app-hml.innova.tv.br');
    url.searchParams.set('pair', pairing.code);
    if (theme.slug) url.searchParams.set('partner', theme.slug);
    return url;
  })();

  return (
    <main className={`login-screen${takeover ? ' login-screen--campaign' : ''}`} data-partner={theme.slug} style={{
      '--partner-accent': theme.primary_color,
      '--partner-secondary': theme.secondary_color,
      backgroundImage: !takeover && theme.login_background_url ? `linear-gradient(90deg, rgba(10,10,12,.92), rgba(10,10,12,.62)), url("${theme.login_background_url}")` : undefined,
    } as CSSProperties}>
      {takeover && <section className="login-screen__media" data-testid="login-ad-media">
        <img className="login-screen__takeover" src={takeover.image}
          alt={takeover.title} onLoad={() => onAdImpression(takeover)} />
        <span className="login-ad-label">PUBLICIDADE</span>
      </section>}
      <div className="login-screen__ambient" />
      <section className={`login-copy${takeover ? ' login-copy--campaign' : ''}`}>
        {!takeover && <BrandMark theme={theme} />}
        {!takeover && <><p className="eyebrow">{theme.login_eyebrow}</p>
          <h1>{theme.login_headline}</h1>
          <p className="login-copy__description">{theme.login_body}</p>
          <div className="login-benefits">
            <span>TV gratuita</span><span>Filmes e séries</span><span>Conteúdo ao vivo</span>
          </div></>}
        {!takeover && theme.sponsor_label && <small className="login-partner-label">{theme.sponsor_label}</small>}
      </section>
      <section className="login-screen__auth">
        <form className="login-card" onSubmit={submit} data-testid="login-form">
          <div className="login-card__brand" data-testid="login-innova-brand"><BrandMark /></div>
          <div className="login-qr">
            <div><span className="login-card__step">ACESSO RÁPIDO</span><h2>Conecte pelo celular</h2><p>Aponte a câmera para o QR Code e confirme sua conta.</p></div>
            <div className="login-qr__code" data-testid="qr-code" data-pairing-code={pairing?.code || ''}>
              {qrImage ? <img src={qrImage} alt="QR Code para conectar a televisão" /> : <span className="spinner" />}
            </div>
            {pairing && <small data-testid="pairing-code">Código {pairing.code.slice(0, 8).toUpperCase()}</small>}
            {pairingError && <small className="login-qr__error">{pairingError}</small>}
          </div>
          <div className="login-divider"><span>ou entre com e-mail</span></div>
          <label>
            <span>E-mail</span>
            <input
              data-focusable="true"
              data-autofocus="true"
              data-testid="email"
              type="email"
              autoComplete="username"
              value={email}
              onChange={event => setEmail(event.target.value)}
              placeholder="voce@exemplo.com"
            />
          </label>
          <label>
            <span>Senha</span>
            <input
              data-focusable="true"
              data-testid="password"
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={event => setPassword(event.target.value)}
              placeholder="••••••••"
            />
          </label>
          {error && <div className="form-error" role="alert">{error}</div>}
          <button data-focusable="true" data-testid="login-submit" className="primary-button" disabled={busy}>
            {busy ? 'Entrando…' : 'Entrar'}
          </button>
          {signupUrl && <a className="pairing-signup" data-testid="pair-signup"
            href={signupUrl.toString()}>Ainda não tenho conta</a>}
        </form>
      </section>
    </main>
  );
}
